import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useSwipeable } from 'react-swipeable'
import styles from './Slideshow.module.scss'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface SlideshowProps {
  slides: React.ReactNode[]
}

export const Slideshow = ({ slides }: SlideshowProps): ReactElement => {
  const [index, setIndex] = useState(0)
  const [direction, setDirection] = useState(1)

  if (slides.length <= 1) return <>{slides[0]}</>

  const nextSlide = useCallback(() => {
    setDirection(1)
    setIndex((prev) => (prev + 1) % slides.length)
  }, [slides.length])

  const prevSlide = useCallback(() => {
    setDirection(-1)
    setIndex((prev) => (prev - 1 + slides.length) % slides.length)
  }, [slides.length])

  useEffect(() => {
    const timer = setTimeout(nextSlide, 10000) // 10 Sekunden bis zum nächsten Slide
    return () => clearTimeout(timer) // Timer bereinigen, wenn sich `index` ändert
  }, [index, nextSlide])

  const handlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    trackMouse: true,
  })

  return (
    <div {...handlers} className={styles.slideshowContainer}>
      <AnimatePresence mode='popLayout' custom={direction}>
        <motion.div
          key={index}
          className={styles.slide}
          initial={{ opacity: 0, x: direction * 100 + '%' }}
          animate={{ opacity: 1, x: '0%' }}
          exit={{ opacity: 0, x: -direction * 100 + '%' }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          {slides[index]}
        </motion.div>
      </AnimatePresence>
      <button onClick={prevSlide} className={styles.prevButton}>
        <ChevronLeft size={24} />
      </button>
      <button onClick={nextSlide} className={styles.nextButton}>
        <ChevronRight size={24} />
      </button>
    </div>
  )
}
